import './styles.scoped.css'
import {useEffect} from 'react'
import {Typography, Button, TextField, Modal, Select, MenuItem, IconButton, FormControl} from '@mui/material'
import {Close} from '@mui/icons-material'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {phoneRegex, emailRegex, parseError} from '../../../../utils/helpers'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {clientApi} from '../../../../services'
import {useMobile} from '../../../../hooks'
import {usStates} from '../../../../utils/helpers'
import {useParams} from 'react-router-dom'
import {AddressAutocompleteField} from '../../../../components'

export default ({open, setOpen, data, loadData}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const {mobile} = useMobile()
    const {clientId} = useParams()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                emergencyName: yup.string(),
                emergencyPhoneNumber: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
                emergencyEmailAddress: yup.string().test('email-optional', 'Invalid email address', value => {
                    if (!value || value.trim() === '') return true
                    return emailRegex.test(value)
                }),
                emergencyRelationship: yup.string(),
                emergencyAddressStreetAddressLine1: yup.string(),
                emergencyAddressStreetAddressLine2: yup.string(),
                emergencyAddressCity: yup.string(),
                emergencyAddressState: yup.string(),
                emergencyAddressZipCode: yup.string().test('zipcode-optional', 'Zip Code must be between 5 and 20 digits', value => {
                    if (!value || value.trim() === '') return true
                    return /^\d{5,20}$/.test(value)
                }),
            }),
        ),
    })

    useEffect(() => {
        form.reset()
        if (data) {
            form.setValue('emergencyName', data?.name)
            form.setValue('emergencyRelationship', data?.relationship)
            form.setValue('emergencyPhoneNumber', data?.phones?.find(item => item.is_primary)?.number)
            form.setValue('emergencyEmailAddress', data?.emails?.find(item => item.is_primary)?.email)
            form.setValue('emergencyAddressStreetAddressLine1', data?.addresses?.find(item => item.is_primary)?.street_line1)
            form.setValue('emergencyAddressStreetAddressLine2', data?.addresses?.find(item => item.is_primary)?.street_line2)
            form.setValue('emergencyAddressCity', data?.addresses?.find(item => item.is_primary)?.city)
            form.setValue('emergencyAddressState', data?.addresses?.find(item => item.is_primary)?.state)
            form.setValue('emergencyAddressZipCode', data?.addresses?.find(item => item.is_primary)?.zip_code)
        }
    }, [data])

    const selectEmergencyAddress = selected => {
        form.setValue('emergencyAddressStreetAddressLine2', '')
        form.setValue('emergencyAddressCity', selected?.city)
        form.setValue('emergencyAddressState', selected?.state)
        form.setValue('emergencyAddressZipCode', selected?.postcode)
    }

    const submit = formData => {
        const body = {
            type: 'emergency',
            name: formData?.emergencyName,
            relationship: formData?.emergencyRelationship,
            phones: [
                {
                    type: 'mobile',
                    number: formData?.emergencyPhoneNumber,
                    is_primary: true,
                },
            ],
            emails: [
                {
                    type: 'personal',
                    email: formData?.emergencyEmailAddress,
                    is_primary: true,
                },
            ],
            addresses: [
                {
                    type: 'home',
                    street_line1: formData?.emergencyAddressStreetAddressLine1,
                    street_line2: formData?.emergencyAddressStreetAddressLine2,
                    city: formData?.emergencyAddressCity,
                    state: formData?.emergencyAddressState,
                    zip_code: formData?.emergencyAddressZipCode,
                    is_primary: true,
                },
            ],
        }
        if (data) {
            setLoading(true)
            clientApi
                .updateClientContact(clientId, data?.id, body)
                .then(() => {
                    setLoading(false)
                    setOpen(false)
                    setMessageBox({
                        status: 'success',
                        title: 'Client Emergency Contact Updated Successfully',
                        description: `The client’s emergency contact details for ${body?.name} have been updated.`,
                    })
                    loadData()
                })
                .catch(error => {
                    setLoading(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        } else {
            setLoading(true)
            clientApi
                .createClientContact(clientId, body)
                .then(() => {
                    setLoading(false)
                    setOpen(false)
                    setMessageBox({
                        status: 'success',
                        title: 'Client Emergency Contact Added Successfully',
                        description: `The client’s emergency contact details for ${body?.name} have been added.`,
                    })
                    loadData()
                })
                .catch(error => {
                    setLoading(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    {data ? 'Update ' : 'Add '}Emergency Contact
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} style={{flex: 1}}>
                                    {data ? 'Update' : 'Add'}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            {data ? 'Update ' : 'Add '}Emergency Contact
                        </Typography>
                    )}
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Name
                    </Typography>
                    <TextField placeholder="Enter your name" fullWidth {...form.register('emergencyName')} />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Relationship
                    </Typography>
                    <TextField placeholder="Enter your relationship" fullWidth {...form.register('emergencyRelationship')} />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Phone Number
                    </Typography>
                    <TextField
                        placeholder="Enter your phone number"
                        fullWidth
                        {...form.register('emergencyPhoneNumber')}
                        error={!!form.formState.errors['emergencyPhoneNumber']}
                        helperText={form.formState.errors['emergencyPhoneNumber']?.message}
                    />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Email Address
                    </Typography>
                    <TextField
                        placeholder="Enter your email address"
                        fullWidth
                        {...form.register('emergencyEmailAddress')}
                        error={!!form.formState.errors['emergencyEmailAddress']}
                        helperText={form.formState.errors['emergencyEmailAddress']?.message}
                    />
                    <br />
                    <br />
                    <Typography variant="overline" color="text.secondary">
                        ADDRESS (OPTIONAL)
                    </Typography>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Street Address Line 1
                    </Typography>
                    <AddressAutocompleteField control={form.control} name="emergencyAddressStreetAddressLine1" onSelect={selectEmergencyAddress} />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Street Address Line 2
                    </Typography>
                    <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('emergencyAddressStreetAddressLine2')} />
                    <br />
                    <br />
                    <div className="form">
                        <div className="form-left">
                            <Typography variant="inputLabel" color="text.secondary">
                                City
                            </Typography>
                            <TextField placeholder="Enter your city" fullWidth {...form.register('emergencyAddressCity')} />
                        </div>
                        <div className="form-right">
                            <Typography variant="inputLabel" color="text.secondary">
                                State
                            </Typography>
                            <FormControl fullWidth>
                                <Controller
                                    {...form.register('emergencyAddressState')}
                                    control={form.control}
                                    render={({field}) => (
                                        <Select
                                            {...field}
                                            value={field.value ?? ''}
                                            onChange={e => field.onChange(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                                anchorOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            displayEmpty
                                            renderValue={selected => {
                                                if (selected === '') {
                                                    return <span style={{color: '#aaa'}}>Select your state</span>
                                                }
                                                return selected
                                            }}>
                                            {usStates.map(item => (
                                                <MenuItem key={item} value={item}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </div>
                    </div>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Zip Code
                    </Typography>
                    <TextField
                        placeholder="Enter your zip code"
                        fullWidth
                        {...form.register('emergencyAddressZipCode')}
                        error={!!form.formState.errors['emergencyAddressZipCode']}
                        helperText={form.formState.errors['emergencyAddressZipCode']?.message}
                    />
                    <br />
                    <br />
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button onClick={() => setOpen(false)} variant="outlined" fullWidth>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)}>
                                {data ? 'Update' : 'Add'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
